import type { KeyboardEvent } from 'react';
import { useCallback, useLayoutEffect, useState } from 'react';

export type UseExternalInputCommandListKeyboardParams = {
  open: boolean;
  /** `CommandItem` `value` props in visual order */
  selectableValues: readonly string[];
  onCommitValue: (itemValue: string) => void;
  /** ArrowDown while closed — open the list (e.g. setOpen + seed search text) */
  onOpenRequest?: () => void;
  loop?: boolean;
};

/**
 * cmdk wires ArrowUp/ArrowDown/Enter to `Command.Input`. When the filter field lives
 * outside `<Command>` (e.g. anchored next to the trigger), sync highlight via controlled
 * `Command` `value` and handle those keys on the external input.
 */
export function useExternalInputCommandListKeyboard({
  open,
  selectableValues,
  onCommitValue,
  onOpenRequest,
  loop = true,
}: UseExternalInputCommandListKeyboardParams) {
  const [highlightedValue, setHighlightedValue] = useState<string | undefined>();

  useLayoutEffect(() => {
    if (!open) {
      setHighlightedValue(undefined);

      return;
    }

    if (selectableValues.length === 0) {
      setHighlightedValue(undefined);

      return;
    }

    setHighlightedValue(prev =>
      prev !== undefined && selectableValues.includes(prev) ? prev : selectableValues[0]!,
    );
  }, [open, selectableValues]);

  const handleListKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();

        if (!open) {
          onOpenRequest?.();

          return;
        }

        if (selectableValues.length === 0) {
          return;
        }

        setHighlightedValue(prev => {
          const i = prev === undefined ? -1 : selectableValues.indexOf(prev);

          if (i === -1) {
            return selectableValues[0]!;
          }

          const next = i + 1 >= selectableValues.length ? (loop ? 0 : i) : i + 1;

          return selectableValues[next]!;
        });

        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();

        if (!open || selectableValues.length === 0) {
          return;
        }

        setHighlightedValue(prev => {
          const i = prev === undefined ? 0 : selectableValues.indexOf(prev);
          const start = i === -1 ? 0 : i;
          const next = start - 1 < 0 ? (loop ? selectableValues.length - 1 : 0) : start - 1;

          return selectableValues[next]!;
        });

        return;
      }

      if (e.key === 'Enter') {
        if (!open || selectableValues.length === 0) {
          return;
        }

        e.preventDefault();

        const current =
          highlightedValue !== undefined && selectableValues.includes(highlightedValue)
            ? highlightedValue
            : selectableValues[0];

        if (current !== undefined) {
          onCommitValue(current);
        }
      }
    },
    [open, selectableValues, highlightedValue, onCommitValue, onOpenRequest, loop],
  );

  return { highlightedValue, setHighlightedValue, handleListKeyDown };
}
